import React, { Dispatch, SetStateAction } from 'react';
import cn from 'classnames';

import { ChipsSelectMenuPlacement } from '@components/common';
import GroupSelectKpi from '@components/common/GroupSelectKpi';
import TreeGroupSelectWrapper from '@components/Kpi/Parameters/ParametersKpiSelector/TreeGroupSelectWrapper';

import { IFlatMoTreeNode } from '@interfaces/components/tree';
import { IGroupSelectOptions, IGroupSelectOptionsKpi } from '@components/common/GroupSelectKpi/GroupSelect.types';

import { KPI_SELECTION_LIMIT_EXCEEDED_TEXT } from '@components/Kpi/Parameters/constants';

import '../Parameters.scss';

interface ParametersKpiSelectorProps {
	isOranVendor: boolean;
	treeKpi: IFlatMoTreeNode[];
	treeCounters: IFlatMoTreeNode[];
	initTreeKpiData: IFlatMoTreeNode[];
	initTreeCountersData: IFlatMoTreeNode[];
	treeKpiIds: string[];
	treeCountersIds: string[];
	handleUpdateTreeKpi: (data: IFlatMoTreeNode[]) => void;
	handleUpdateTreeCounters: (data: IFlatMoTreeNode[]) => void;
	kpiValues?: IGroupSelectOptions[];
	optionsKpi: IGroupSelectOptionsKpi[];
	onChangeKpi: (values: IGroupSelectOptionsKpi[], isUpdateDirty?: boolean) => void;
	setTreeKpi: Dispatch<SetStateAction<IFlatMoTreeNode[]>>;
	setTreeCounters: Dispatch<SetStateAction<IFlatMoTreeNode[]>>;
	setInitCountersData: (nodes: IFlatMoTreeNode[]) => void;
	setTreeCountersIds: (ids: string[]) => void;
	setInitKpiData: (nodes: IFlatMoTreeNode[]) => void;
	setTreeKpiIds: (ids: string[]) => void;
	setIsDirty: Dispatch<SetStateAction<boolean>>;
	setSelectedKpiNumber: Dispatch<SetStateAction<number>>;
	setSelectedCountersNumber: Dispatch<SetStateAction<number>>;
	maxSelection?: number | null;
	maxSelectionText?: string;
	isMaxSelectionLimitErrorMessageShown: boolean;
	isMaxSelectionLimitErrorMessageCountersShown: boolean;
	selectedKpiNumber: number;
	selectedCountersNumber: number;
	isRequired?: boolean;
}

const ParametersKpiSelector: React.FC<ParametersKpiSelectorProps> = ({
	isOranVendor,
	treeKpi,
	treeCounters,
	initTreeKpiData,
	initTreeCountersData,
	treeKpiIds,
	treeCountersIds,
	handleUpdateTreeKpi,
	handleUpdateTreeCounters,
	kpiValues = [],
	optionsKpi,
	onChangeKpi,
	setTreeKpi,
	setTreeCounters,
	setInitCountersData,
	setTreeCountersIds,
	setInitKpiData,
	setTreeKpiIds,
	setIsDirty,
	setSelectedKpiNumber,
	setSelectedCountersNumber,
	maxSelection,
	maxSelectionText,
	isMaxSelectionLimitErrorMessageShown,
	isMaxSelectionLimitErrorMessageCountersShown,
	selectedCountersNumber,
	isRequired = true,
}) => {
	return (
		<div className='f5gParametersKpi'>
			<div className={cn('f5gParametersBodyLabel', {
				'f5gParametersBodyLabelRequired': isRequired,
			})}>
				{isOranVendor ? 'Source' : 'KPI'}
			</div>
			{isOranVendor
				? <>
					<TreeGroupSelectWrapper
						data={treeCounters}
						initData={initTreeCountersData}
						isIgnoreTreeChecked
						isPortal
						label='Select performance counters'
						maxSelectionNumber={maxSelection}
						maxSelectionText={maxSelectionText}
						menuPlacement={ChipsSelectMenuPlacement.bottomEnd}
						onSubmit={handleUpdateTreeCounters}
						searchPlaceholder='Search by performance counters'
						selectedKpiNumber={treeCountersIds.length}
						setData={setTreeCounters}
						setInitData={setInitCountersData}
						setIsDirty={setIsDirty}
						setSelectedKpiNumber={setSelectedCountersNumber}
						setTreeKpiIds={setTreeCountersIds}
						title='Select performance counters'
					/>
					{isMaxSelectionLimitErrorMessageCountersShown
						&& <div className='f5gParametersKpiErrorText'>
							You have exceeded the maximum selection
							limit ({selectedCountersNumber}/{maxSelection}).
							Please remove existing selections to stay within the limit.
						</div>
					}
					<br />
					<TreeGroupSelectWrapper
						data={treeKpi}
						initData={initTreeKpiData}
						isIgnoreTreeChecked
						isPortal
						maxSelectionNumber={maxSelection}
						maxSelectionText={maxSelectionText}
						menuPlacement={ChipsSelectMenuPlacement.bottomEnd}
						onSubmit={handleUpdateTreeKpi}
						selectedKpiNumber={treeKpiIds.length}
						setData={setTreeKpi}
						setInitData={setInitKpiData}
						setIsDirty={setIsDirty}
						setSelectedKpiNumber={setSelectedKpiNumber}
						setTreeKpiIds={setTreeKpiIds}
					/>
				</>
				: <GroupSelectKpi
					kpiValues={kpiValues}
					label='Select KPI '
					maxSelectionNumber={maxSelection}
					maxSelectionText={maxSelectionText}
					menuPlacement='top'
					name='KPI'
					onChange={onChangeKpi}
					options={optionsKpi}
					searchPlaceholder='Search by KPI'
					setSelectedItemsNumber={setSelectedKpiNumber}
				/>
			}
			{isMaxSelectionLimitErrorMessageShown
				&& <div className='f5gParametersKpiErrorText'>
					{KPI_SELECTION_LIMIT_EXCEEDED_TEXT}
				</div>
			}
		</div>
	);
};

export default ParametersKpiSelector;
